import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {useTranslation} from 'react-i18next';
import {Check, ShieldCheck, TriangleAlert, UserRound} from 'lucide-react';
import {StepTracker, cn, type StepTrackerStep} from '@chekinapp/ui';
import {useSlowAfter} from 'hooks';
import type {RegistrationPhase} from './useRegistrationResultStore';
import styles from './RegistrationResult.module.css';

const SLOW_AFTER_MS = 8000;
export const DETAILS_SENT_MS = 450;
export const CONFIRMING_MS = 350;
const RING_SIZE = 148;
const RING_STROKE = 8;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;

export type RegisteringViewHandle = {
  getRects: () => {ring?: DOMRect; title?: DOMRect};
};

type Props = {
  phase: Extract<RegistrationPhase, 'sending' | 'succeeded' | 'errored'>;
  firstName: string;
  offers?: ReactNode;
  succeededTitle?: string;
  footnote?: ReactNode;
};

function useElapsed(ms: number, isRunning: boolean) {
  const [hasElapsed, setHasElapsed] = useState(false);

  useEffect(() => {
    if (!isRunning) return;
    setHasElapsed(false);
    const timeout = setTimeout(() => setHasElapsed(true), ms);
    return () => clearTimeout(timeout);
  }, [ms, isRunning]);

  return hasElapsed;
}

function getStepStates(
  phase: Props['phase'],
  isSent: boolean,
  isConfirmed: boolean,
): StepTrackerStep['state'][] {
  if (phase === 'errored') return ['done', 'failed', 'todo'];
  if (phase === 'succeeded') {
    return isConfirmed ? ['done', 'done', 'done'] : ['done', 'done', 'active'];
  }
  return isSent ? ['done', 'active', 'todo'] : ['active', 'todo', 'todo'];
}

const RegisteringView = forwardRef<RegisteringViewHandle, Props>(
  ({phase, firstName, offers, succeededTitle, footnote}, ref) => {
    const {t} = useTranslation();
    const isSlow = useSlowAfter(SLOW_AFTER_MS);
    const ringRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);

    useImperativeHandle(ref, () => ({
      getRects: () => ({
        ring: ringRef.current?.getBoundingClientRect(),
        title: titleRef.current?.getBoundingClientRect(),
      }),
    }));

    const isSent = useElapsed(DETAILS_SENT_MS, true);
    const isConfirmed = useElapsed(CONFIRMING_MS, phase === 'succeeded');
    const isCompact = Boolean(offers);
    const isSucceeded = phase === 'succeeded' && isConfirmed;
    const isErrored = phase === 'errored';
    const isSending = !isSucceeded && !isErrored;
    const [sent, registering, confirmed] = getStepStates(phase, isSent, isConfirmed);

    const steps: StepTrackerStep[] = [
      {id: 'sent', label: t('step_details_sent'), state: sent},
      {id: 'registering', label: t('step_registering'), state: registering},
      {id: 'confirmed', label: t('step_confirmed'), state: confirmed},
    ];

    const title = isSucceeded
      ? succeededTitle ?? t('guest_is_registered_short', {name: firstName})
      : isErrored
      ? t('something_went_wrong')
      : t('registering_guest', {name: firstName});

    const subtitle = isSucceeded
      ? t('all_set')
      : isErrored
      ? t('couldnt_finish_registering', {name: firstName})
      : isSlow
      ? t('submission_step_registering_slow')
      : t('registering_hint');

    return (
      <div className="flex min-h-[calc(var(--app-dvh,100dvh)-160px)] flex-1 flex-col items-center text-center lg:min-h-[480px]">
        <div
          className={cn(
            'flex w-full flex-1 flex-col items-center',
            isCompact ? 'justify-start pt-2' : 'justify-center',
          )}
        >
          <div
            ref={ringRef}
            className={cn(
              'relative grid place-items-center',
              isCompact ? 'size-[88px]' : 'size-[148px]',
            )}
            data-phase={phase}
          >
            <svg
              aria-hidden
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              className="absolute inset-0 size-full"
            >
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                className={cn(
                  'transition-colors duration-300',
                  isSucceeded
                    ? 'stroke-[var(--chekin-success)]'
                    : isErrored
                    ? 'stroke-[var(--chekin-danger-soft)]'
                    : 'stroke-[var(--chekin-border-default)]',
                )}
              />
              {isSending && (
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  fill="none"
                  strokeWidth={RING_STROKE}
                  strokeLinecap="round"
                  strokeDasharray="110 267"
                  className={cn('stroke-[var(--chekin-color-brand-blue)]', styles.spin)}
                />
              )}
            </svg>
            {isSucceeded ? (
              <Check
                aria-hidden
                strokeWidth={2.6}
                className={cn(
                  'text-[var(--chekin-success-text)]',
                  isCompact ? 'size-8' : 'size-12',
                )}
              />
            ) : isErrored ? (
              <TriangleAlert
                aria-hidden
                className={cn(
                  'text-[var(--chekin-danger-soft)]',
                  isCompact ? 'size-7' : 'size-10',
                )}
              />
            ) : (
              <UserRound
                aria-hidden
                className={cn(
                  'text-[var(--chekin-color-brand-blue)]',
                  isCompact ? 'size-7' : 'size-10',
                )}
              />
            )}
          </div>

          <h1
            ref={titleRef}
            aria-live="polite"
            className={cn(
              'text-[22px] font-semibold leading-[29px] text-[var(--chekin-color-brand-navy)]',
              isCompact ? 'mt-4' : 'mt-6',
            )}
          >
            {title}
          </h1>
          <p className="mt-1.5 max-w-[360px] text-[14px] leading-[19px] text-[var(--chekin-text-secondary)]">
            {subtitle}
          </p>

          <StepTracker
            appearance="solid"
            steps={steps}
            label={t('registration')}
            className="mt-[22px] w-full max-w-[300px]"
          />
          {offers && <div className="mt-8 w-full">{offers}</div>}
        </div>

        {!isCompact && (
          <p className="flex max-w-[260px] flex-col items-center gap-1.5 pb-[calc(40px+env(safe-area-inset-bottom))] pt-6 text-[12px] font-medium leading-[17px] text-[var(--chekin-text-secondary)]">
            <ShieldCheck aria-hidden className="size-5 text-[var(--chekin-green-700)]" />
            {footnote ?? t('saved_on_device_until_confirm')}
          </p>
        )}
      </div>
    );
  },
);
RegisteringView.displayName = 'RegisteringView';

export {RegisteringView};
